import {
  defineDoctype,
  type DoctypeCatalogMetadata,
  type DoctypeDefinition,
} from "@kubdee/sdk";
import { marketingCampaigns } from "./schemas/campaigns";
import { marketingContentPosts } from "./schemas/content-posts";
import { marketingMediaAssets } from "./schemas/media-assets";
import { marketingPostInsights } from "./schemas/post-insights";
import { marketingPostMedia } from "./schemas/post-media";
import { marketingPostTargets } from "./schemas/post-targets";
import { marketingPublishLogs } from "./schemas/publish-logs";

// ============================================================================
// Marketing module — registered doctypes (#786)
//
// Six user-facing doctypes, plus a protected junction classification for
// `marketing_post_media` so the physical table is covered by the catalog audit.
//
// Submittable kind: `content_post` follows the standard 0/1/2 doc_status
// triad (see schema). Read-only kinds: `publish_log`, `post_insight` —
// both append-only audit / metrics surfaces.
// ============================================================================

const STANDARD_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const READ_ONLY_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const MEDIA_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: false,
  },
};

const JUNCTION_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

/**
 * Campaign (`แคมเปญ`) — bundles posts under a theme. Master-data style:
 * normal CRUD, searchable, no numbering.
 */
export const marketingCampaignDoctype = defineDoctype({
  id: "campaign",
  moduleId: "marketing",
  table: marketingCampaigns,
  label: "แคมเปญ",
  icon: "Megaphone",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: STANDARD_CATALOG,
  displayField: "name",
  searchFields: ["name", "description", "status"],
  columnLabels: {
    company_id: "บริษัท",
    name: "ชื่อแคมเปญ",
    description: "คำอธิบาย",
    status: "สถานะ",
    starts_at: "เริ่มแคมเปญ",
    ends_at: "สิ้นสุดแคมเปญ",
  },
});

/**
 * Content Post (`โพสต์คอนเทนต์`) — the post itself. Submittable lifecycle
 * (draft → submitted → approved → published) backed by `doc_status`.
 */
export const marketingContentPostDoctype = defineDoctype({
  id: "content_post",
  moduleId: "marketing",
  table: marketingContentPosts,
  label: "โพสต์คอนเทนต์",
  icon: "FileText",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: STANDARD_CATALOG,
  displayField: "caption",
  searchFields: ["caption", "link_url", "status"],
  columnLabels: {
    company_id: "บริษัท",
    campaign_id: "แคมเปญ",
    caption: "ข้อความ",
    link_url: "ลิงก์",
    status: "สถานะ",
    scheduled_for: "กำหนดเผยแพร่",
    doc_status: "สถานะเอกสาร",
    submitted_at: "ส่งเมื่อ",
    submitted_by: "ผู้ส่ง",
  },
});

/**
 * Post Target (`ปลายทางการโพสต์`) — per-channel fan-out row. Lives + dies
 * with its parent post (CASCADE on delete in the schema).
 */
export const marketingPostTargetDoctype = defineDoctype({
  id: "post_target",
  moduleId: "marketing",
  table: marketingPostTargets,
  label: "ปลายทางการโพสต์",
  icon: "Send",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: STANDARD_CATALOG,
  displayField: "provider_account_id",
  searchFields: [
    "provider",
    "provider_account_id",
    "provider_post_id",
    "status",
  ],
  columnLabels: {
    company_id: "บริษัท",
    post_id: "โพสต์",
    connector_instance_id: "การเชื่อมต่อ",
    provider: "ผู้ให้บริการ",
    provider_account_id: "บัญชีผู้ให้บริการ",
    status: "สถานะ",
    provider_post_id: "รหัสโพสต์ผู้ให้บริการ",
    provider_url: "ลิงก์โพสต์",
    error_code: "รหัสข้อผิดพลาด",
    error_message: "ข้อความผิดพลาด",
    attempts: "จำนวนครั้งที่ลอง",
    last_attempted_at: "เผยแพร่ครั้งล่าสุดเมื่อ",
    published_at: "เผยแพร่เมื่อ",
    idempotency_key: "Idempotency Key",
  },
});

/**
 * Media Asset (`สื่อ`) — uploaded image / video / carousel container.
 */
export const marketingMediaAssetDoctype = defineDoctype({
  id: "media_asset",
  moduleId: "marketing",
  table: marketingMediaAssets,
  label: "สื่อ",
  icon: "ImageIcon",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MEDIA_CATALOG,
  displayField: "url",
  searchFields: ["kind", "mime_type", "alt_text", "provider_media_id"],
  columnLabels: {
    company_id: "บริษัท",
    kind: "ประเภท",
    url: "URL ไฟล์",
    mime_type: "ประเภท MIME",
    file_size_bytes: "ขนาดไฟล์ (bytes)",
    width: "ความกว้าง",
    height: "ความสูง",
    duration_ms: "ความยาว (ms)",
    provider_media_id: "รหัสสื่อผู้ให้บริการ",
    alt_text: "ข้อความทดแทน",
  },
});

/**
 * Post Media (`สื่อของโพสต์`) — protected junction between content posts and
 * reusable media assets. It is catalogued for schema coverage, not standalone
 * CRUD.
 */
export const marketingPostMediaDoctype = defineDoctype({
  id: "post_media",
  moduleId: "marketing",
  table: marketingPostMedia,
  label: "สื่อของโพสต์",
  icon: "Images",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "junction",
  catalog: JUNCTION_CATALOG,
  numberingField: null,
  displayField: "post_id",
  imageField: null,
  searchFields: ["post_id", "media_id"],
  columnLabels: {
    post_id: "โพสต์",
    media_id: "สื่อ",
    position: "ลำดับ",
  },
});

/**
 * Publish Log (`บันทึกการเผยแพร่`) — append-only audit trail per target.
 * Marked read-only so the doctype catalog blocks generic update/delete.
 */
export const marketingPublishLogDoctype = defineDoctype({
  id: "publish_log",
  moduleId: "marketing",
  table: marketingPublishLogs,
  label: "บันทึกการเผยแพร่",
  icon: "ScrollText",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "ledger",
  catalog: READ_ONLY_CATALOG,
  displayField: "event",
  searchFields: ["event", "provider_post_id", "error_code"],
  columnLabels: {
    company_id: "บริษัท",
    target_id: "ปลายทาง",
    event: "เหตุการณ์",
    provider_post_id: "รหัสโพสต์ผู้ให้บริการ",
    provider_url: "ลิงก์โพสต์",
    error_code: "รหัสข้อผิดพลาด",
    error_message: "ข้อความผิดพลาด",
    payload_json: "Payload",
  },
});

/**
 * Post Insight (`ข้อมูลเชิงลึก`) — engagement metrics snapshot. Read-only.
 */
export const marketingPostInsightDoctype = defineDoctype({
  id: "post_insight",
  moduleId: "marketing",
  table: marketingPostInsights,
  label: "ข้อมูลเชิงลึก",
  icon: "BarChart3",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "ledger",
  catalog: READ_ONLY_CATALOG,
  displayField: "snapshotted_at",
  searchFields: ["snapshotted_at", "provider_post_id"],
  columnLabels: {
    company_id: "บริษัท",
    target_id: "ปลายทาง",
    provider_post_id: "รหัสโพสต์ผู้ให้บริการ",
    snapshotted_at: "บันทึกเมื่อ",
    impressions: "การเข้าถึง (Impressions)",
    reach: "ผู้ใช้ที่เข้าถึง (Reach)",
    likes: "ถูกใจ",
    comments: "ความคิดเห็น",
    shares: "การแชร์",
    clicks: "คลิก",
    engagement_rate: "อัตราการมีส่วนร่วม",
    raw_payload: "Payload ดิบ",
  },
});

/**
 * All marketing doctypes registered with the platform. The order is
 * preserved in the Admin UI via `listDoctypes()` sort.
 */
export const marketingDoctypes: readonly DoctypeDefinition[] = Object.freeze([
  marketingCampaignDoctype,
  marketingContentPostDoctype,
  marketingPostTargetDoctype,
  marketingMediaAssetDoctype,
  marketingPostMediaDoctype,
  marketingPublishLogDoctype,
  marketingPostInsightDoctype,
]);
